import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { History, Plus, Pencil, Trash2, ShieldAlert, RotateCcw, Archive } from "lucide-react";
import { HeaderAccent } from "@/lib/status";
import { useAuth } from "@/context/AuthContext";

const ACTION_META = {
  create: { icon: Plus, cls: "bg-sage-light text-sage-dark", label: "Created" },
  update: { icon: Pencil, cls: "bg-slateblue-light text-slateblue", label: "Updated" },
  delete: { icon: Trash2, cls: "bg-coral-light text-coral-dark", label: "Deleted" },
  blocked_checkin: { icon: ShieldAlert, cls: "bg-gold-light text-gold-dark", label: "Blocked" },
};

export default function Activity() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [archived, setArchived] = useState([]);
  const [view, setView] = useState("live"); // live | archive

  const load = () => api.get("/audit").then((r) => setRows(r.data)).catch(() => {});
  const loadArchive = () => api.get("/audit/archive").then((r) => setArchived(r.data)).catch(() => {});
  useEffect(() => { load(); if (isAdmin) loadArchive(); }, [isAdmin]);

  const del = async (id) => {
    try { await api.delete(`/audit/${id}`); setRows((l) => l.filter((r) => r.id !== id)); if (isAdmin) loadArchive(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const clearAll = async () => {
    if (!window.confirm("Clear the activity log? Entries are archived for 90 days (for compliance) and can be restored, then removed automatically.")) return;
    try { await api.delete("/audit"); setRows([]); loadArchive(); toast.success("Activity log cleared and archived for 90 days"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const restore = async (id) => {
    try { await api.post(`/audit/archive/${id}/restore`); toast.success("Entry restored to the log"); setArchived((l) => l.filter((r) => r.id !== id)); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const list = view === "live" ? rows : archived;

  return (
    <div className="space-y-6" data-testid="activity-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">Activity log</h1>
          <p className="mt-1 text-ink-soft">Who created, edited or removed clients and visits.</p>
          <HeaderAccent />
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <div className="flex rounded-lg border border-border p-0.5" data-testid="activity-view-toggle">
              <button onClick={() => setView("live")} data-testid="view-live"
                className={`rounded-md px-3 py-1.5 text-sm font-medium ${view === "live" ? "bg-brand text-white" : "text-ink-soft hover:bg-secondary"}`}>Live</button>
              <button onClick={() => setView("archive")} data-testid="view-archive"
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ${view === "archive" ? "bg-brand text-white" : "text-ink-soft hover:bg-secondary"}`}>
                <Archive size={14} /> Cleared ({archived.length})
              </button>
            </div>
          )}
          {isAdmin && view === "live" && rows.length > 0 && (
            <button onClick={clearAll} data-testid="clear-audit-button"
              className="inline-flex items-center gap-2 rounded-lg border border-coral px-4 py-2.5 text-sm font-semibold text-coral transition-colors hover:bg-coral hover:text-white">
              <Trash2 size={16} /> Clear log
            </button>
          )}
        </div>
      </div>

      {view === "archive" && (
        <p className="rounded-lg border border-gold/40 bg-gold-light/50 px-4 py-2.5 text-sm text-gold-dark" data-testid="archive-notice">
          Cleared entries are retained for 90 days for compliance, then deleted automatically. You can restore any entry back to the live log.
        </p>
      )}

      <div className="overflow-hidden rounded-xl border border-border bg-white shadow-sm">
        {list.length === 0 && <p className="p-8 text-center text-ink-muted">{view === "live" ? "No activity recorded yet." : "No cleared entries."}</p>}
        {list.map((r) => {
          const m = ACTION_META[r.action] || ACTION_META.update;
          const Icon = m.icon;
          return (
            <div key={r.id} data-testid={`audit-row-${r.id}`} className="group flex items-center gap-4 border-b border-border px-5 py-3.5 last:border-0">
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${m.cls}`}><Icon size={16} /></span>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-ink"><span className="font-semibold">{r.summary}</span></p>
                <p className="text-xs text-ink-muted">{r.user_name} ({r.user_role}) · {r.entity}</p>
              </div>
              <span className="shrink-0 text-xs text-ink-muted">{new Date(r.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
              {isAdmin && view === "live" && (
                <button onClick={() => del(r.id)} data-testid={`delete-audit-${r.id}`} title="Archive & remove"
                  className="shrink-0 rounded-full p-1.5 text-ink-muted opacity-0 transition-opacity hover:bg-coral-light hover:text-coral group-hover:opacity-100">
                  <Trash2 size={15} />
                </button>
              )}
              {isAdmin && view === "archive" && (
                <button onClick={() => restore(r.id)} data-testid={`restore-audit-${r.id}`}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-sage px-3 py-1.5 text-xs font-semibold text-white hover:brightness-105">
                  <RotateCcw size={13} /> Restore
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
